import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { CheckCircle2, Circle, Rocket, X, Send } from "lucide-react";

export function OnboardingChecklist() {
  const [data, setData] = useState(null);
  const [sending, setSending] = useState(false);
  const load = () => api.get("/onboarding/checklist").then(({ data }) => setData(data)).catch(() => {});
  useEffect(() => { load(); }, []);
  if (!data || data.dismissed || data.total === 0 || data.done === data.total) return null;
  const pct = Math.round((data.done / data.total) * 100);
  const dismiss = async () => { await api.post("/onboarding/checklist/dismiss?dismissed=true"); setData({ ...data, dismissed: true }); };
  const sendRoster = async () => {
    setSending(true);
    try { const { data: r } = await api.post("/onboarding/send-roster"); toast.success(`Driver list sent to Britt & Jenn — ${r.active} active of ${r.drivers} drivers`); load(); }
    catch (e) { toast.error(formatApiError(e)); } finally { setSending(false); }
  };
  return (
    <Card className="p-6 mb-6 border-2 border-fuchsia-300 bg-gradient-to-r from-fuchsia-50 via-white to-lime-50" data-testid="onboarding-checklist">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="tag-mono text-fuchsia-700 flex items-center gap-1"><Rocket className="h-3.5 w-3.5" />GET SET UP</div>
          <h2 className="text-xl font-bold font-heading text-slate-900">Finish setting up your compliance profile</h2>
          <p className="text-sm text-slate-600 mt-1">Five quick steps and we can track, remind and file everything for you.</p>
        </div>
        <Button variant="ghost" size="sm" onClick={dismiss} className="text-slate-500" data-testid="onboarding-dismiss"><X className="h-4 w-4 mr-1" />Dismiss</Button>
      </div>
      <div className="mt-4 flex items-center gap-3">
        <div className="flex-1 h-4 rounded-full bg-slate-200 overflow-hidden ring-2 ring-white shadow-inner" data-testid="onboarding-progress-track">
          <div className="h-full rounded-full bg-gradient-to-r from-fuchsia-500 via-orange-400 to-lime-400 transition-[width] duration-700 ease-out shadow-[0_0_12px_rgba(217,70,239,.55)]" style={{ width: `${Math.max(pct, 4)}%` }} data-testid="onboarding-progress-bar" />
        </div>
        <div className="font-mono font-bold text-fuchsia-700 whitespace-nowrap" data-testid="onboarding-progress-label">{data.done} of {data.total} complete</div>
      </div>
      <div className="grid md:grid-cols-2 xl:grid-cols-5 gap-3 mt-5">
        {data.items.map((it, i) => (
          <div key={it.key} className={`rounded-lg border p-4 flex flex-col gap-2 ${it.done ? "border-lime-300 bg-lime-50/70" : "border-slate-200 bg-white"}`} data-testid={`onboarding-item-${it.key}`}>
            <div className="flex items-center gap-2 font-semibold text-slate-900">{it.done ? <CheckCircle2 className="h-5 w-5 text-lime-600" /> : <Circle className="h-5 w-5 text-fuchsia-400" />}<span className="font-mono text-xs text-slate-400">0{i + 1}</span>{it.label}</div>
            <p className="text-xs text-slate-600 flex-1">{it.hint}</p>
            {!it.done && it.action === "send-roster" && <Button size="sm" onClick={sendRoster} disabled={sending} className="bg-fuchsia-600 hover:bg-fuchsia-700 self-start" data-testid="onboarding-send-roster"><Send className="h-3.5 w-3.5 mr-1" />{sending ? "Sending…" : "Send driver list"}</Button>}
            {!it.done && !it.action && <Button asChild size="sm" className="bg-fuchsia-600 hover:bg-fuchsia-700 self-start" data-testid={`onboarding-go-${it.key}`}><Link to={it.to}>Do it →</Link></Button>}
            {it.done && <span className="text-xs font-semibold text-lime-700">Done{it.sent_at ? ` · sent ${new Date(it.sent_at).toLocaleDateString()}` : ""}</span>}
          </div>
        ))}
      </div>
    </Card>
  );
}
